import { useState } from 'react';
import { skillsAPI, conexionesAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, ClipboardPaste, AlertTriangle, XCircle, CheckCircle2, KeyRound, Plug, Wrench, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Pegar **lo que uno tenga** sobre una API y que quede configurada.
 *
 * **Por qué el importador de curl no alcanzaba.** Diego lo midió con un caso real: intentó
 * conectar la API de reportes de la empresa con lo que tenía —la ficha de Postman, con su bloque
 * de OAuth2— y la puerta pedía *"un curl que ya funcione"*. **Pedir un curl que funcione es
 * pedirle a alguien que ya resolvió el problema**: el que ya lo tiene andando es el que menos
 * necesita esto.
 *
 * **Se propone, no se aplica.** Lo que vuelve es un borrador que se revisa y se edita antes de
 * guardar. Un importador que guarda solo produce configuración que nadie leyó, y el día que falla
 * nadie sabe por qué estaba así.
 *
 * **Dónde terminan las credenciales, dicho con precisión.** El texto las trae —de eso se trata— y
 * el backend las separa del borrador. Vuelven una vez, para poder crear la conexión: no es una
 * exposición nueva, la persona acaba de pegarlas en este mismo navegador. Lo que **nunca** pasa es
 * que queden en la herramienta o en la prosa de la habilidad, que se exportan, se versionan y se
 * muestran en pantalla — y ése era el riesgo real.
 */
export default function PegarLoQueTengas({ tenantId, capsuleId, onListo }) {
  const [texto, setTexto] = useState('');
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [creada, setCreada] = useState('');
  const [r, setR] = useState(null);

  const leer = async () => {
    setCargando(true);
    setR(null);
    try {
      const { data } = await skillsAPI.importarLoQueTengas({
        texto, tenant_id: tenantId || '', capsule_id: capsuleId || '',
      });
      setR(data);
    } catch (err) {
      setR({ ok: false, error: formatApiError(err) || 'No se pudo leer' });
    } finally {
      setCargando(false);
    }
  };

  /** Crea la conexión con sus credenciales. La herramienta y la prosa quedan para revisar. */
  const crearLaConexion = async () => {
    const c = r?.propuesta?.conexion || {};
    if (!c.nombre_corto || !c.base_url) {
      toast.error('Falta el nombre corto o la URL base: completalos antes de crearla.');
      return;
    }
    setGuardando(true);
    try {
      await conexionesAPI.save(c.nombre_corto, {
        tenant_id: tenantId,
        capsule_id: '',
        nombre: c.nombre || c.nombre_corto,
        descripcion: '',
        tipo: 'rest',
        base_url: c.base_url,
        auth_modo: c.auth_modo || 'ninguna',
        auth_nombre_del_campo: c.auth_nombre_del_campo || '',
        token_url: c.token_url || '',
        scope: c.scope || '',
        auth_del_cliente: c.auth_del_cliente || 'cuerpo',
        // El esquema sale del modo: es lo que hace que el formulario sepa qué pedir después.
        config_schema: detectadas.map((k) => ({
          key: k, label: k, type: 'string', required: true,
          secret: k.includes('SECRET') || k.includes('TOKEN') || k === 'CLAVE',
        })),
        config_values: r.credenciales || {},
        prueba: {},
        plantilla_id: 'propia',
        activa: true,
      });
      setCreada(c.nombre_corto);
      toast.success(`Conexión «${c.nombre_corto}» creada con sus credenciales`);
      onListo?.(r.propuesta);
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo crear la conexión');
    } finally {
      setGuardando(false);
    }
  };

  const p = r?.propuesta || {};
  const detectadas = Object.keys(r?.credenciales || {});

  return (
    <Card className="rounded-sm border-brand-border" data-testid="pegar-lo-que-tengas">
      <CardHeader className="py-2 px-3 border-b border-brand-border">
        <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-600 flex items-center gap-1">
          <ClipboardPaste className="w-3 h-3" /> Pegar lo que tengas
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-3">
        <p className="text-[10px] text-zinc-500">
          La ficha de Postman, un curl, la documentación o unas notas. <b>Con las claves</b>: van
          cifradas a la conexión, y nunca al documento de la habilidad. De ahí sale la conexión, la
          herramienta y el texto que le dice al agente cuándo usarla — después lo editás.
        </p>

        <textarea
          data-testid="texto-pegado"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          className="w-full h-40 border border-brand-border rounded-sm p-2 font-mono text-[11px] leading-relaxed focus:ring-1 focus:ring-brand-primary focus:outline-none resize-y"
          placeholder={'GET Libro Banco DESA\nURL: https://portal.ejemplo.com/api/v1/libro-banco\nParams: fecha (hasta), dias (se resta para el desde)\n\nAuthorization: OAuth 2.0\n  Grant Type = Client Credentials\n  Access Token URL = https://portal.ejemplo.com/api/oauth/token\n  Client ID = ...\n  Client Secret = ...\n  Client Authentication = Send as Basic Auth header'}
        />

        <Button
          size="sm" data-testid="leer-lo-pegado"
          className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7 px-3"
          onClick={leer} disabled={cargando || texto.trim().length < 20}
        >
          {cargando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
          Leerlo y proponer
        </Button>

        {r && !r.ok && (
          <div data-testid="pegar-error"
               className="text-[11px] rounded-sm border border-red-200 bg-red-50 text-red-800 p-2 flex gap-2">
            <XCircle className="w-4 h-4 shrink-0" /><div>{r.error}</div>
          </div>
        )}

        {r?.ok && (
          <div className="space-y-2">
            {/* **Las credenciales: cuáles, nunca sus valores.** Decir que se detectaron es lo que
                evita que alguien las vuelva a pegar en otro lado creyendo que se perdieron. */}
            {detectadas.length > 0 && (
              <div className="text-[11px] rounded-sm border border-green-200 bg-green-50 text-green-900 p-2 flex gap-2">
                <KeyRound className="w-4 h-4 shrink-0" />
                <div>
                  Detecté <b>{detectadas.join(', ')}</b>. Van <b>cifradas a la conexión</b> al
                  crearla, y no quedan en la herramienta ni en el texto de la habilidad — que son
                  los que se exportan y se muestran.
                </div>
              </div>
            )}

            <div className="rounded-sm border border-brand-border p-2 space-y-2 text-[11px]">
              <div className="flex items-start gap-2">
                <Plug className="w-3.5 h-3.5 mt-0.5 text-zinc-400 shrink-0" />
                <div className="flex-1">
                  <b>{p.conexion?.nombre || p.conexion?.nombre_corto || '(sin nombre)'}</b>
                  <div className="font-mono text-[10px] text-zinc-500">{p.conexion?.base_url}</div>
                  <div className="text-[10px] text-zinc-500">
                    {p.conexion?.auth_modo}
                    {p.conexion?.auth_del_cliente ? ` · credencial ${p.conexion.auth_del_cliente}` : ''}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 border-t border-brand-border/60 pt-2">
                <Wrench className="w-3.5 h-3.5 mt-0.5 text-zinc-400 shrink-0" />
                <div className="flex-1">
                  <b className="font-mono">{p.herramienta?.nombre}</b>{' '}
                  <Badge variant="outline" className="text-[9px] rounded-sm px-1 py-0">
                    {p.herramienta?.metodo}
                  </Badge>
                  <div className="font-mono text-[10px] text-zinc-500">{p.herramienta?.ruta}</div>
                  <div className="text-zinc-600">{p.herramienta?.descripcion}</div>
                  {(p.herramienta?.parametros || []).map((x, i) => (
                    <div key={i} className="text-[10px] text-zinc-500">
                      · <span className="font-mono">{x.nombre}</span> ({x.ubicacion}
                      {x.requerido ? ', obligatorio' : ''}) — {x.descripcion}
                    </div>
                  ))}
                </div>
              </div>

              {p.prosa?.cuando_usarla && (
                <div className="flex items-start gap-2 border-t border-brand-border/60 pt-2">
                  <FileText className="w-3.5 h-3.5 mt-0.5 text-zinc-400 shrink-0" />
                  <div className="text-zinc-600 whitespace-pre-wrap">{p.prosa.cuando_usarla}</div>
                </div>
              )}
            </div>

            {/* Lo que no se pudo deducir. **Esconderlo daría un borrador que parece listo y no lo
                está**, que cuesta más que uno con huecos señalados. */}
            {(r.no_pude_deducir || []).map((a, i) => (
              <div key={i} data-testid="no-pude-deducir"
                   className="text-[11px] rounded-sm border border-amber-200 bg-amber-50/60 text-amber-900 p-2 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" /><div>{a}</div>
              </div>
            ))}

            {/* **Qué quedó hecho y qué falta, separado.** La primera vez que se usó esto, la
                conexión se creó bien y apareció un error rojo de la herramienta: los dos pasos se
                veían como uno solo y la persona concluyó que no había funcionado nada. Decir cuál
                de los dos anduvo es la diferencia entre seguir y volver a empezar. */}
            {creada && (
              <div data-testid="conexion-creada"
                   className="text-[11px] rounded-sm border border-green-200 bg-green-50 text-green-900 p-2 flex gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <div>
                  <b>La conexión «{creada}» quedó creada</b>, con sus credenciales cifradas.
                  <div className="opacity-80">
                    La herramienta se agregó abajo para que la revises. Si aparece algo en rojo,
                    es de la herramienta — <b>la conexión ya está</b>. Probala en <b>Conexiones</b>.
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm" data-testid="crear-la-conexion"
                onClick={crearLaConexion} disabled={guardando || !!creada}
                className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7 px-3"
              >
                {guardando ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                           : <CheckCircle2 className="w-3 h-3 mr-1" />}
                Crear la conexión
              </Button>
              <span className="text-[10px] text-zinc-500">
                Después probala en <b>Conexiones</b>: si el proveedor rechaza la credencial, el
                botón te dice si el problema es cómo viaja.
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
